/**
 * BroadcastDedupCard — Apr 29, 2026 (Yash).
 *
 * Settings → Admin Tools card that runs the one-time historical broadcast
 * deduplication. Two-step UX:
 *   1. "Scan duplicates" → calls /admin/dedupe-broadcasts?dry_run=true,
 *      shows counts + first few groups so the admin sanity-checks.
 *   2. After scan, "Delete X duplicates" appears, with a confirm dialog.
 *
 * Companion to iteration 110's _broadcast_already_stored helper which
 * prevents NEW duplicates. This card cleans up the pre-existing ones.
 */
import { useState } from "react";
import { Loader2, Search, Trash2, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const BroadcastDedupCard = () => {
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [scan, setScan] = useState(null);   // dry_run result
  const [deleted, setDeleted] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runScan = async () => {
    setScanning(true);
    setDeleted(null);
    try {
      const res = await api.post("/admin/dedupe-broadcasts?dry_run=true");
      setScan(res.data);
      if (res.data?.duplicates_found === 0) {
        toast.success("No duplicate broadcasts found — nothing to clean.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const runDelete = async () => {
    setDeleting(true);
    try {
      const res = await api.post("/admin/dedupe-broadcasts?dry_run=false");
      setDeleted(res.data);
      setConfirmOpen(false);
      // Refresh scan numbers (should drop to 0)
      setScan(null);
      toast.success(`Deleted ${res.data?.deleted || 0} duplicate broadcasts`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bento-card" data-testid="broadcast-dedup-card">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-amber-500 to-rose-500 text-white shadow-sm">
            <AlertTriangle size={18} />
          </div>
          <div>
            <h2 className="text-xl font-medium text-[#1C1917]" style={{ fontFamily: "Playfair Display, serif" }}>
              Cleanup Duplicate Broadcasts
            </h2>
            <p className="text-[11px] text-[--text-muted] leading-tight mt-0.5">
              One-time scan + delete of historical broadcasts that landed twice
              on the same lead due to WATI cross-path delivery (now fixed
              forward).
            </p>
          </div>
        </div>
      </div>

      {/* Step 1 — scan */}
      {!scan && !deleted && (
        <button
          onClick={runScan}
          disabled={scanning}
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-[13px] py-2.5 rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all disabled:opacity-50"
          data-testid="dedup-scan-btn"
        >
          {scanning ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
          {scanning ? "Scanning…" : "Scan for duplicate broadcasts (safe — read-only)"}
        </button>
      )}

      {/* Step 2 — show scan results + delete button */}
      {scan && !deleted && (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Stat label="Scanned" value={scan.scanned} />
            <Stat label="Duplicate groups" value={scan.groups_with_duplicates} />
            <Stat label="Rows to delete" value={scan.duplicates_found} accent />
          </div>

          {scan.duplicates_found > 0 ? (
            <>
              {scan.sample_groups?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 max-h-56 overflow-y-auto" data-testid="dedup-preview">
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-amber-800 mb-2">Preview (first {scan.sample_groups.length} of {scan.groups_with_duplicates} groups)</p>
                  <div className="space-y-1.5">
                    {scan.sample_groups.map((g, i) => (
                      <div key={g.kept_id || g.lead_id || `dup-${i}`} className="text-[10.5px] bg-white border border-amber-100 rounded-lg p-2">
                        <p className="text-slate-800">
                          <span className="font-extrabold text-amber-700">{g.total_copies}× copies</span> · lead <span className="font-mono text-slate-500">{(g.lead_id || "").slice(0, 8)}…</span> · keeping <span className="font-mono text-emerald-600">{(g.kept_id || "").slice(0, 8)}…</span>
                        </p>
                        <p className="text-slate-500 italic mt-0.5 line-clamp-2">"{g.content_preview}"</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!confirmOpen ? (
                <div className="flex gap-2">
                  <button
                    onClick={runScan}
                    disabled={scanning}
                    className="flex-1 bg-white border border-slate-300 hover:border-slate-400 text-slate-700 font-bold text-[12px] py-2 rounded-xl flex items-center justify-center gap-1.5 transition-all"
                    data-testid="dedup-rescan-btn"
                  >
                    {scanning ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Re-scan
                  </button>
                  <button
                    onClick={() => setConfirmOpen(true)}
                    className="flex-1 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white font-bold text-[12px] py-2 rounded-xl flex items-center justify-center gap-1.5 shadow-sm transition-all"
                    data-testid="dedup-delete-btn"
                  >
                    <Trash2 size={12} /> Delete {scan.duplicates_found} duplicates
                  </button>
                </div>
              ) : (
                <div className="bg-rose-50 border-2 border-rose-200 rounded-xl p-3" data-testid="dedup-confirm-dialog">
                  <p className="text-[12px] font-extrabold text-rose-800 mb-1">⚠ Are you sure?</p>
                  <p className="text-[11px] text-rose-700 mb-3">
                    This will permanently delete <span className="font-bold">{scan.duplicates_found}</span> duplicate broadcast messages from <span className="font-bold">{scan.groups_with_duplicates}</span> chats. The OLDEST copy is kept in each group. This cannot be undone.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmOpen(false)}
                      disabled={deleting}
                      className="flex-1 bg-white border border-slate-300 text-slate-700 font-bold text-[12px] py-2 rounded-lg hover:bg-slate-50 transition-all"
                      data-testid="dedup-cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={runDelete}
                      disabled={deleting}
                      className="flex-1 bg-rose-600 hover:bg-rose-700 text-white font-bold text-[12px] py-2 rounded-lg flex items-center justify-center gap-1.5 transition-all disabled:opacity-50"
                      data-testid="dedup-confirm-delete-btn"
                    >
                      {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      {deleting ? "Deleting…" : "Yes, delete"}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 flex items-center gap-2 text-[11px] text-emerald-800">
              <Check size={13} /> No duplicates found — your broadcasts are clean.
            </div>
          )}
        </>
      )}

      {/* Step 3 — done */}
      {deleted && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-2" data-testid="dedup-success">
          <Check size={14} className="text-emerald-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-[12px] font-bold text-emerald-800">Cleanup complete</p>
            <p className="text-[11px] text-emerald-700">Deleted {deleted.deleted} duplicate broadcasts.</p>
            <button onClick={() => { setDeleted(null); setScan(null); }} className="text-[10px] font-bold text-emerald-700 hover:text-emerald-900 mt-1.5" data-testid="dedup-reset-btn">
              Run another scan
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-xl border p-2 text-center ${accent ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"}`}>
    <p className="text-[9px] font-extrabold uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-[18px] font-extrabold ${accent ? "text-rose-700" : "text-slate-800"}`} style={{ fontFamily: "Outfit" }}>
      {(value ?? 0).toLocaleString()}
    </p>
  </div>
);

export default BroadcastDedupCard;
